import React, { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';
import { RouteComponentProps } from 'react-router-dom';
import { Box, CircularProgress } from '@material-ui/core';

import FullPageLayout from '../_layout/FullPageLayout/full-page.layout';
import { GET_PUBLIC_PLAYER_HEADER } from '../../../lib/queries/attendance-response.queries';
import { PublicEditPlayerHeader } from './public-edit-player-header.component';
import { PublicEditPlayerProfile } from './public-edit-player-profile.component';
import { PublicEditPlayerRsvp } from './public-edit-player-rsvp.component';
import { RsvpIdentityBanner } from './rsvp-identity-banner.component';
import { PublicAttendanceTable } from './public-attendance-table.component';
import { PublicEditPlayerUnauthorized } from './public-edit-player-unauthorized.component';
import { ParentContactsSectionContainer } from './parent-contacts-section.container';
import { useRejectedAccessLink } from '../../../lib/hooks/use-rejected-access-link';
import { PARENT_HOME_ROUTE } from '../../../../_configuration';

// Path params for `/parent/player/:code/:playerAssessmentId`. `code` keys the
// profile editor's read; the token credential arrives on the query string.
type PublicEditPlayerRouteParams = {
  code: string;
  playerAssessmentId: string;
};

// Public, token-authenticated landing page. Two views: the attendance "dashboard"
// (one row per upcoming ice time) and a focused RSVP view for a single session.
// The URL's `assessmentSessionId` seeds the initial view — present means the
// link is a per-session deep-link (email button / SMS), absent means the parent
// arrived at the general dashboard.
export const PublicEditPlayerPage: React.FC<RouteComponentProps<PublicEditPlayerRouteParams>> = ({ match, location, history }) => {
  const { code, playerAssessmentId } = match.params;
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const statusParamFromUrl = searchParams.get('status');
  const initialAssessmentSessionIdFromUrl = searchParams.get('assessmentSessionId');

  // The deep-link params seed state once on mount but are *cleared* on back
  // navigation so re-selecting any row from the table yields a fresh picker
  // view — never the pre-selected confirm view from the original email link.
  const [initialAssessmentSessionId, setInitialAssessmentSessionId] = useState<string | null>(initialAssessmentSessionIdFromUrl);
  const [statusParam, setStatusParam] = useState<string | null>(statusParamFromUrl);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(initialAssessmentSessionIdFromUrl);
  // Player-details editor is hidden by default; the header's Edit button
  // toggles it open. Closed view is just the summary header + attendance table.
  const [editing, setEditing] = useState(false);

  // Auth probe: cheap header query (the route gate guarantees a credential is
  // present). `cache-and-network` — credentialContact derives from the server's
  // contact-claiming sweep, which happens outside this client's cache.
  const { data: headerData, error: tokenError, loading: tokenLoading } = useQuery(GET_PUBLIC_PLAYER_HEADER, {
    variables: { playerAssessmentId, token },
    fetchPolicy: 'cache-and-network',
  });
  const { rejectedLink, signInPath, redirecting } = useRejectedAccessLink({ token, error: tokenError, loading: tokenLoading });

  const handleBack = useCallback(() => {
    setSelectedSessionId(null);
    setInitialAssessmentSessionId(null);
    setStatusParam(null);
    // Strip `assessmentSessionId` + `status` from the URL so a refresh on the
    // table view doesn't re-enter the focused RSVP view. `token` is kept —
    // it's the page's credential.
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete('assessmentSessionId');
    nextParams.delete('status');
    history.replace({ pathname: location.pathname, search: nextParams.toString() ? `?${nextParams.toString()}` : '' });
  }, [location, history]);

  // Same chrome on every render path so the loader/unauthorized states sit in
  // the same shell as the loaded page. Layout owns the parent-tree green strip
  // (and ignores `parentHeader` on the legacy /public/edit-player mount).
  const parentHeader = { title: 'Player Details', backTo: PARENT_HOME_ROUTE, backLabel: 'My players' };

  // Only gate on the FIRST load; cache-and-network re-flags `loading` on every
  // background refetch, which would flash a spinner over data already on screen.
  if ((tokenLoading && !headerData) || redirecting) {
    return (
      <FullPageLayout parentHeader={parentHeader}>
        <Box textAlign="center">
          <CircularProgress size={28} />
        </Box>
      </FullPageLayout>
    );
  }

  if (rejectedLink) {
    return (
      <FullPageLayout parentHeader={parentHeader}>
        <PublicEditPlayerUnauthorized reason={rejectedLink} signInPath={signInPath} />
      </FullPageLayout>
    );
  }

  return (
    <FullPageLayout parentHeader={parentHeader}>
      <PublicEditPlayerHeader
        playerAssessmentId={playerAssessmentId}
        token={token}
        editing={editing}
        onToggleEdit={() => setEditing((e) => !e)}
      />
      {/* Sits under the header on BOTH views — the Edit toggle rides on the
          header, so gating the panel on the dashboard view stranded the button. */}
      {editing && (
        <PublicEditPlayerProfile
          code={code}
          playerAssessmentId={playerAssessmentId}
          token={token}
        />
      )}
      {selectedSessionId ? (
        <>
          {/* Scoped to the focused RSVP view: the warning is about whose name
              the response is recorded under, so back-to-ice-times clears it. */}
          <RsvpIdentityBanner credentialContact={headerData?.publicPlayerHeader?.credentialContact} />
          <PublicEditPlayerRsvp
            playerAssessmentId={playerAssessmentId}
            token={token}
            assessmentSessionId={selectedSessionId}
            // `statusParam` is an email-button deep-link signal (`?status=attending`).
            // It only seeds the INITIAL focused view. Once the parent has navigated
            // back and picked another row, no choice is ever pre-selected — preserves
            // the "never auto-confirm a choice the parent didn't make" property
            // (see "The public RSVP page never submits on load" in CLAUDE.md).
            statusParam={selectedSessionId === initialAssessmentSessionId ? statusParam : null}
            onBack={handleBack}
          />
        </>
      ) : (
        <>
          <ParentContactsSectionContainer playerAssessmentId={playerAssessmentId} />
          <PublicAttendanceTable
            playerAssessmentId={playerAssessmentId}
            token={token}
            onSelectSession={setSelectedSessionId}
          />
        </>
      )}
    </FullPageLayout>
  );
};
